/**
 * ChecklistSection + ChecklistRow — extracted from DesignPackTab.js
 * (v260.76). Pure mechanical move; no behaviour change.
 */
import React, { useState, useMemo } from 'react';
import {
  FileText, AlertCircle, FileCheck2,
} from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { Textarea } from '../../../components/ui/textarea';
import { SectionCard, STATUS_META, CATEGORY_META, fmtDateTime } from './shared';

function ChecklistRow({ item, onUpdate, disabled, nested = false }) {
  const [notes, setNotes] = useState(item.notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const meta = STATUS_META[item.status] || STATUS_META.not_started;
  const CatIcon = (CATEGORY_META[item.category] || {}).icon || FileText;
  const catTone = (CATEGORY_META[item.category] || {}).tone || 'bg-slate-100 text-slate-700';

  const handleStatusChange = async (newStatus) => {
    if (updatingStatus || disabled) return;
    setUpdatingStatus(true);
    try {
      await onUpdate(item.id, { status: newStatus });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const saveNotes = async () => {
    if (notes === (item.notes || '') || disabled) return;
    setSavingNotes(true);
    try {
      await onUpdate(item.id, { notes });
    } finally {
      setSavingNotes(false);
    }
  };

  return (
    <div
      className={`border border-slate-100 rounded-md p-3 bg-white ${nested ? 'ml-6 border-l-2 border-l-slate-200' : ''}`}
      data-testid={`design-pack-checklist-row-${item.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={`text-[9px] py-0 ${catTone}`}>
              <CatIcon className="w-2.5 h-2.5 mr-0.5 inline" />
              {item.category}
            </Badge>
            <span className="text-sm font-medium text-[#0F2040]">{item.deliverable}</span>
            {item.is_milestone && <Badge className="bg-amber-100 text-amber-700 text-[9px] py-0">Milestone</Badge>}
            {item.role && <Badge variant="secondary" className="text-[9px] py-0">{item.role}</Badge>}
            {item.hours != null && <span className="text-[10px] text-slate-400">{item.hours}h</span>}
          </div>
          {item.why && <div className="text-xs text-slate-600 mt-1 leading-relaxed">{item.why}</div>}
          {/* Deliverable assumptions — only shown when present, with a
              distinct amber tint so consultants don't miss them. */}
          {item.assumptions && item.assumptions.length > 0 && (
            <ul className="mt-1.5 space-y-0.5">
              {item.assumptions.map((a, k) => (
                <li key={k} className="text-[10px] text-amber-700 italic flex items-start gap-1" data-testid={`design-pack-checklist-assumption-${item.id}-${k}`}>
                  <span className="text-amber-500 mt-0.5">•</span>
                  <span>Assumption: {a}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex-shrink-0">
          <select
            value={item.status}
            disabled={disabled || updatingStatus}
            onChange={e => handleStatusChange(e.target.value)}
            data-testid={`design-pack-checklist-status-${item.id}`}
            className={`text-[11px] font-medium border rounded-md px-2 py-1 cursor-pointer ${meta.tone} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {Object.entries(STATUS_META).map(([k, m]) => (
              <option key={k} value={k}>{m.label}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="mt-2">
        <Textarea
          value={notes}
          disabled={disabled}
          onChange={e => setNotes(e.target.value)}
          onBlur={saveNotes}
          placeholder={disabled ? 'Finalize the pack to add notes' : 'Add a note (saved on blur)…'}
          rows={1}
          className="text-xs resize-none"
          data-testid={`design-pack-checklist-notes-${item.id}`}
        />
        {item.updated_by_name && (
          <div className="text-[10px] text-slate-400 mt-1">
            Last updated by {item.updated_by_name} · {fmtDateTime(item.updated_at)}
            {savingNotes && <span className="ml-1 italic">saving…</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function ChecklistSection({ checklist, onUpdate, disabled }) {
  const [filter, setFilter] = useState('all'); // all | not_started | in_progress | blocked | complete

  // v260.62 — Filter first, then group into (group → sub_group →
  // component rows + their deliverable children). Sign-off & loose
  // rows (no parent_component AND not a Component) flow into a final
  // "Other" bucket.
  const filtered = useMemo(() => {
    if (filter === 'all') return checklist;
    return checklist.filter(c => c.status === filter);
  }, [checklist, filter]);

  // Build a parent → children lookup so deliverable rows can render
  // indented under their owning component row. Children only appear
  // here if BOTH parent and child survive the status filter — that
  // matches how a real triage list reads.
  const grouped = useMemo(() => {
    const idIndex = new Map(filtered.map(it => [it.id, it]));
    // Map child rows by parent component name. Used to nest
    // deliverables under their parent component when both pass the
    // current filter.
    const childrenByParent = new Map();
    for (const it of filtered) {
      if (it.category === 'Deliverable' && it.parent_component) {
        if (!childrenByParent.has(it.parent_component)) {
          childrenByParent.set(it.parent_component, []);
        }
        childrenByParent.get(it.parent_component).push(it);
      }
    }
    // Top-level group → sub_group → ordered list of [component, [deliv,…]] OR loose row
    const out = new Map();
    for (const it of filtered) {
      // Skip deliverable rows that we'll render nested under their parent.
      if (it.category === 'Deliverable' && it.parent_component && filtered.some(p => p.deliverable === it.parent_component && p.category === 'Component')) {
        continue;
      }
      const g = it.group || 'Other';
      const sg = it.sub_group || '';
      if (!out.has(g)) out.set(g, new Map());
      if (!out.get(g).has(sg)) out.get(g).set(sg, []);
      const children = it.category === 'Component'
        ? (childrenByParent.get(it.deliverable) || [])
        : [];
      out.get(g).get(sg).push({ row: it, children });
    }
    return Array.from(out.entries()).map(([group, subMap]) => ({
      group,
      subs: Array.from(subMap.entries()).map(([sg, rows]) => ({ sub_group: sg, rows })),
    }));
  }, [filtered]);

  // Per-status counts for the filter chip bar
  const counts = useMemo(() => {
    const out = { all: checklist.length };
    for (const k of Object.keys(STATUS_META)) out[k] = 0;
    for (const c of checklist) out[c.status] = (out[c.status] || 0) + 1;
    return out;
  }, [checklist]);

  return (
    <SectionCard id="checklist" icon={FileCheck2} title="Implementation checklist" count={checklist.length} defaultOpen>
      <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid="design-pack-checklist-filter-bar">
        {['all', 'not_started', 'in_progress', 'blocked', 'complete'].map(f => {
          const label = f === 'all' ? 'All' : STATUS_META[f].label;
          const active = filter === f;
          return (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              data-testid={`design-pack-checklist-filter-${f}`}
              className={`text-[10px] px-2 py-1 rounded-full border ${
                active
                  ? 'border-[#0F2040] bg-[#0F2040] text-white'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
              }`}
            >
              {label} <span className={active ? 'opacity-80' : 'text-slate-400'}>·  {counts[f] || 0}</span>
            </button>
          );
        })}
      </div>
      {disabled && (
        <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-[11px] text-amber-800 flex items-start gap-2" data-testid="design-pack-checklist-disabled-notice">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          The checklist becomes editable once you finalize the Handover Pack. Until then, this is a preview of what the implementation team will see.
        </div>
      )}
      <div className="space-y-4">
        {grouped.length === 0 ? (
          <p className="text-xs text-slate-400 italic">No items match this filter.</p>
        ) : (
          grouped.map((g, gi) => (
            <div key={g.group} data-testid={`design-pack-checklist-group-${gi}`}>
              <div className="flex items-center gap-2 mb-2 pb-1.5 border-b border-slate-200">
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#0F2040]">{g.group}</span>
                <span className="text-[10px] text-slate-400">
                  {g.subs.reduce((sum, s) => sum + s.rows.reduce((a, r) => a + 1 + r.children.length, 0), 0)} item{(g.subs.reduce((sum, s) => sum + s.rows.reduce((a, r) => a + 1 + r.children.length, 0), 0)) === 1 ? '' : 's'}
                </span>
              </div>
              {g.subs.map(({ sub_group, rows }, si) => (
                <div key={sub_group || `_${si}`} className={si > 0 ? 'mt-3' : ''}>
                  {sub_group && (
                    <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5 ml-1 font-semibold">{sub_group}</div>
                  )}
                  <div className="space-y-2">
                    {rows.map(({ row, children }) => (
                      <React.Fragment key={row.id}>
                        <ChecklistRow item={row} onUpdate={onUpdate} disabled={disabled} />
                        {children.length > 0 && (
                          <div className="space-y-1.5">
                            {children.map(child => (
                              <ChecklistRow key={child.id} item={child} onUpdate={onUpdate} disabled={disabled} nested />
                            ))}
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </SectionCard>
  );
}

// ─── Finalize Modal ───────────────────────────────────────────────────


export { ChecklistRow };
export default ChecklistSection;
